import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { isRenderableImageUrl } from "../is-renderable-image-url";
import { AdPreviewsResponse, GoogleAdPreview } from "../../pages/marketing/ads-preview/types";

/**
 * Scrub ad imagery at the data boundary: cached backend payloads have
 * historically carried comma-split URL fragments (AUT-16) in `images` /
 * `imageUrl`, which rendered as broken tiles. The preview components already
 * degrade gracefully when an ad has no imagery.
 */
function sanitizeAdPreviews(data: AdPreviewsResponse): AdPreviewsResponse {
  return {
    ...data,
    ads: (data.ads ?? []).map((ad) => ({
      ...ad,
      imageUrl: isRenderableImageUrl(ad.imageUrl) ? ad.imageUrl : undefined,
      images: ad.images?.filter(isRenderableImageUrl),
    })),
  };
}

/**
 * Mock data for development/testing when backend is not available
 * Set useMockData to true to use this data instead of API calls
 */
const useMockData = false;

const mockAdPreviews: AdPreviewsResponse = {
  isConnected: true,
  lastSyncedAt: new Date().toISOString(),
  ads: [
    {
      adId: "mock-1",
      adGroupName: "Brand Campaign",
      finalUrl: "https://example-hotel.com/rooms",
      displayName: "Example Hotel",
      displayDomain: "example-hotel.com",
      faviconUrl: "https://www.google.com/s2/favicons?domain=marriott.com&sz=64",
      path: "rooms",
      headlines: ["Luxury Beachfront Rooms", "Book Direct & Save 15%", "5-Star Oceanview Suites"],
      descriptions: [
        "Experience world-class hospitality at our oceanfront resort. Spacious rooms, stunning views, exceptional service.",
        "Free cancellation • Best rate guarantee • Members earn double points",
      ],
      imageUrl: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400",
      siteLinks: [
        { title: "View Rooms & Suites", url: "https://example-hotel.com/rooms" },
        { title: "Special Offers", url: "https://example-hotel.com/offers" },
        { title: "Dining & Spa", url: "https://example-hotel.com/amenities" },
        { title: "Meetings & Events", url: "https://example-hotel.com/events" },
      ],
    },
    {
      adId: "mock-2",
      adGroupName: "Seasonal Promo",
      finalUrl: "https://example-hotel.com/summer-special",
      displayName: "Example Hotel",
      displayDomain: "example-hotel.com",
      faviconUrl: "https://www.google.com/s2/favicons?domain=marriott.com&sz=64",
      path: "summer-special",
      headlines: ["Summer Escape Sale", "Up to 30% Off Stays", "Limited Time Offer"],
      descriptions: [
        "Book your summer getaway now and save big. Includes complimentary breakfast and late checkout.",
      ],
      imageUrl: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400",
      siteLinks: [
        { title: "Book Now", url: "https://example-hotel.com/book" },
        { title: "View Details", url: "https://example-hotel.com/summer-special" },
      ],
    },
    {
      adId: "mock-3",
      adGroupName: "Generic Search",
      finalUrl: "https://example-hotel.com",
      displayName: "Example Hotel",
      displayDomain: "example-hotel.com",
      faviconUrl: "https://www.google.com/s2/favicons?domain=marriott.com&sz=64",
      imageUrl: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400",
      headlines: ["Downtown Boutique Hotel", "Walking Distance to Attractions"],
      descriptions: [
        "Stylish accommodations in the heart of the city. Modern amenities, rooftop bar, award-winning restaurant.",
      ],
    },
  ],
};

/**
 * Custom hook to fetch Google Ads preview data
 *
 * Follows the project pattern of wrapping useQuery in custom hooks.
 * Uses the marketing/ad-previews endpoint to get visual preview data for ads.
 *
 * The backend returns 404 while `adGenerationStatus` is "generating" / null —
 * that's expected, not an error — so callers in the onboarding flow MUST
 * gate this hook via the `enabled` parameter to avoid spamming the console
 * with 404s. Pass `enabled: adGenerationStatus === "ready"` from the caller.
 * The marketing page caller (post-onboarding) does not need to gate.
 *
 * @param userId - The user ID to fetch ad previews for
 * @param enabled - Whether to fire the request. Defaults to `true` for the
 *                  marketing page; the onboarding caller should pass the
 *                  ad-generation readiness check here.
 * @returns Query result with ad preview data, loading state, and error state
 *
 * @example
 * const { data, isLoading, error, refetch } = useAdPreviews(userId, isReady);
 * if (isLoading) return <Spinner />;
 * if (error) return <ErrorState onRetry={refetch} />;
 * if (!data?.ads?.length) return <EmptyState />;
 * return <AdCarousel ads={data.ads} />;
 */
export function useAdPreviews(userId?: string, enabled: boolean = true) {
  return useQuery<AdPreviewsResponse>({
    queryKey: ["ad-previews", userId],
    queryFn: async () => {
      // Use mock data for development if enabled
      if (useMockData) {
        // Simulate network delay
        await new Promise((resolve) => setTimeout(resolve, 800));
        return mockAdPreviews;
      }

      if (!userId) {
        throw new Error("User ID is required");
      }
      return sanitizeAdPreviews(await api.getAdPreviews(userId));
    },
    enabled: enabled && (!!userId || useMockData),
    // Cache for 5 minutes - ad preview data doesn't change frequently
    staleTime: 5 * 60 * 1000,
    // Keep previous data while refetching
    keepPreviousData: true,
  });
}

/**
 * Helper type for components that receive ad preview data
 */
export type { GoogleAdPreview, AdPreviewsResponse, SiteLink } from "../../pages/marketing/ads-preview/types";
